import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import WebSearchSetting from "./WebSearchSetting";
import {CampaignService} from "../../service/CampaignService";
import {Panel} from "primereact/panel";
import JobSchedulingTimeSetting from "./JobSchedulingTimeSetting";
import {AppContext} from "../../HomePage";
import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {MultiSelect} from "primereact/multiselect";
import {Dropdown} from "primereact/dropdown";
import {SpecialFieldService} from "../../service/SpecialFieldService";
import {search_fields} from "../../utils/Constants";

/**
 * A per-host `search_fields_based_url` record that is always safe to spread over.
 *
 * Two shapes have to survive: a legacy record that is a bare array of fields
 * (spreading it would inject numeric keys), and a record written before any
 * search field was picked (no `fields` at all, which makes the scraper's
 * get_search_field_codes fail for the whole site search job). Both become a
 * dict carrying a `fields` list.
 */
export const normalizeHostSearchConfig = (existing) => {
    if (Array.isArray(existing))
        return {fields: existing};
    if (!existing || typeof existing !== 'object')
        return {fields: []};
    return {...existing, fields: Array.isArray(existing.fields) ? existing.fields : []};
};

/**
 * A `site_budgets` host key is always the bare, lowercase hostname with no
 * `www.` prefix, however the value was written -- mirrors the backend's own
 * `scraper.site_budget.normalize_host` exactly (lowercase, tolerate a bare
 * host with no scheme, strip a leading "www." only). The admin types a bare
 * host here while the search config holds a full URL with a trailing slash;
 * comparing those two spellings literally is how a mismatch here previously
 * misfiled 29 records, so the write and the eventual backend read must use
 * the identical rule.
 */
export const normalizeHost = (urlOrHost) => {
    if (!urlOrHost || !String(urlOrHost).trim())
        return '';
    let text = String(urlOrHost).trim().toLowerCase();
    if (!text.includes('//'))
        text = '//' + text;
    let host = '';
    try {
        host = new URL(text, 'https://placeholder.invalid').hostname;
    } catch (e) {
        host = '';
    }
    return host.startsWith('www.') ? host.slice(4) : host;
};

/**
 * Writes one host's two independent daily-request quotas -- `search` and
 * `refresh` are separate endpoints and must never be merged into one field
 * or one number. A blank value for a kind (null/undefined/'') removes that
 * key rather than writing 0: an absent kind means "no cap" to the backend,
 * an explicit 0 means "configured and exhausted", and the admin clearing a
 * field must never be read as the second of those. A host left with neither
 * kind set is dropped entirely so it falls back to "no cap" like any other
 * untouched host.
 */
export const buildSiteBudgets = (existing, hostOrUrl, values) => {
    const host = normalizeHost(hostOrUrl);
    const budgets = {...(existing || {})};
    const entry = {};

    if (values) {
        if (values.search !== undefined && values.search !== null && values.search !== '')
            entry.search = values.search;
        if (values.refresh !== undefined && values.refresh !== null && values.refresh !== '')
            entry.refresh = values.refresh;
    }

    if (Object.keys(entry).length === 0) {
        delete budgets[host];
        return budgets;
    }

    budgets[host] = entry;
    return budgets;
};

/**
 * Whether a single budget value ('search' or 'refresh') counts as a real,
 * configured cap. `0` is a real cap meaning "fetch nothing today" and must
 * count as set; `undefined`/`null` means "no cap" and must not. Plain
 * truthiness (`if (value)`) gets this backwards for exactly the `0` case, so
 * every caller that needs this distinction goes through here instead.
 */
export const isBudgetValueSet = (value) => typeof value === 'number';

/**
 * Whether a host's `site_budgets` entry (`{search, refresh}`, possibly
 * undefined) has either quota configured. Shared by the Sites screen's row
 * tag and its edit dialog so "is this host capped" is decided in exactly one
 * place -- duplicating the check is how `0` quietly ends up read as "no
 * budget" in one of the two spots.
 */
export const isHostBudgeted = (entry) => !!entry && (isBudgetValueSet(entry.search) || isBudgetValueSet(entry.refresh));

/**
 * Sums one host's `kind` budget across campaigns. Nothing on the backend
 * infers or caps the account-wide total -- the admin owns each campaign's
 * number individually -- so without this sum an over-allocation across
 * campaigns for the same host is invisible until Farmazon starts rejecting
 * requests. A campaign with no `site_budgets`, or none for this host and
 * kind, contributes 0.
 */
export const totalForHost = (campaigns, hostOrUrl, kind) => {
    const host = normalizeHost(hostOrUrl);
    return (campaigns || []).reduce((sum, c) => {
        const value = c?.settings?.site_budgets?.[host]?.[kind];
        return sum + (typeof value === 'number' ? value : 0);
    }, 0);
};

/**
 * `totalForHost` over the account's campaigns, but only when the account is
 * actually known. `contextCampaigns` is whatever `CampaignContext.campaigns`
 * holds for the logged-in user -- correct on the customer's own settings
 * screens, but on the global cross-user Admin > Job Scheduling table the
 * campaign being edited belongs to some OTHER account, and the logged-in
 * admin's own campaign list has nothing to do with it. Summing that list
 * would silently produce a confidently wrong number -- one that tells the
 * admin they are safe to add more budget when a sibling campaign of the
 * REAL account already spends toward the same host. So this returns `null`,
 * meaning "cannot be computed here", whenever the campaign being edited is
 * not itself found in `contextCampaigns` (by id) -- i.e. whenever
 * `contextCampaigns` is proven to be the wrong account's list. Never fetches
 * anything to fill the gap: no endpoint for "all campaigns of an arbitrary
 * account" exists for this task, so the honest move is to say so instead of
 * guessing.
 */
export const totalForHostIfKnown = (contextCampaigns, campaign, hostOrUrl, kind) => {
    const campaigns = contextCampaigns || [];
    if (!campaign || !campaigns.some(c => c.id === campaign.id))
        return null;

    const campaignsForTotal = campaigns
        .filter(c => c.id !== campaign.id)
        .concat([campaign]);

    return totalForHost(campaignsForTotal, hostOrUrl, kind);
};

const JobSchedulingSiteSearchSetting = (props) => {

    const {t, setError} = React.useContext(AppContext);
    const [loading, setLoading] = useState(false);


    const [campaign, setCampaign] = useState(props.campaign);
    const [searchCompetitors, setSearchCompetitors] = useState(campaign.settings && campaign.settings.site_search_job && campaign.settings.site_search_job.competitor_urls ? [...campaign.settings.site_search_job.competitor_urls.map(c => ({url: c}))] : []);
    function getSelectedFieldNames(fields) {
        return Array.isArray(fields) ? fields.map(field => field.name ? field.name : field) : [];
    }

    function getSelectedFieldCodes(fields) {
        return Array.isArray(fields) ? fields.map(field => field.code ? field.code : field) : [];
    }

    const [campaignCompetitors, setCampaignCompetitors] = useState(() => {
        const competitors = campaign.settings?.site_search_job?.competitor_urls || [];
        const searchFieldsMap = campaign.settings?.site_search_job?.search_fields_based_url || {};
        const matchFieldsMap = campaign.settings?.site_search_job?.match_fields_based_url || {};

        return competitors.map(url => ({
            url,
            selected_fields: getSelectedFieldCodes(searchFieldsMap[url]?.fields || searchFieldsMap[url]),
            selected_match_fields: getSelectedFieldNames(matchFieldsMap[url]?.fields || matchFieldsMap[url]),
            target: searchFieldsMap[url]?.target || 'competitor',
        }));
    });
    const [matchFields, setMatchFields] = useState([]);
    const campaignService = new CampaignService();
    const specialFieldService = new SpecialFieldService();
    let site_search_job = campaign.settings && campaign.settings.site_search_job ? campaign.settings.site_search_job : {}

    const getMatchFields = (specialFields) => {
        const productCodes = [{name: 'sku'}, {name: 'mpn'}, {name: 'ean'}, {name: 'upc'}, {name: 'asin'}, {name: 'barcode'}, {name: 'search_key'}, {name: 'brand'}, {name: 'category'}];
        return productCodes.concat(specialFields || []);
    }

    useEffect(() => {
        campaign.settings.site_search_job = site_search_job
        if (campaign.id) {
            setLoading(true);

            Promise.all([
                campaignService.getCampaignCompetitors(campaign.id),
                specialFieldService.getSpecialFields()
            ])
                .then(([competitors, specialFields]) => {

                    const siteSearchJob = campaign.settings?.site_search_job || {};
                    const searchFieldsMap = siteSearchJob.search_fields_based_url || {};
                    const matchFieldsMap = siteSearchJob.match_fields_based_url || {};

                    const prepared = competitors
                        .filter(comp => comp.is_site_searching_ready)
                        .map(c => ({
                            ...c,
                            selected_fields: getSelectedFieldCodes(searchFieldsMap[c.url]?.fields || searchFieldsMap[c.url]),
                            selected_match_fields: getSelectedFieldNames(matchFieldsMap[c.url]?.fields || matchFieldsMap[c.url]),
                            target: searchFieldsMap[c.url]?.target || 'competitor',
                        }));

                    setMatchFields(getMatchFields(specialFields));
                    setCampaignCompetitors(prepared);
                    setLoading(false);
                })
                .catch(error => {
                    setError(error);
                    setError(null);
                    setLoading(false);
                });
        }
    }, []);

    const urlTemplate = (rowData) => <a target="_blank" rel="noopener noreferrer" href={rowData.url}>{rowData.url}</a>;
    const searchFieldsTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <MultiSelect
                value={Array.isArray(rowData.selected_fields) ? rowData.selected_fields : []}
                options={search_fields}
                optionLabel="name"
                optionValue="code"
                onChange={(e) => handleSearchFieldChange(rowData.url, e.value)}
            />
        </div>
    );

    const matchFieldsTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <MultiSelect
                value={Array.isArray(rowData.selected_match_fields) ? rowData.selected_match_fields : []}
                options={matchFields}
                optionLabel="name"
                optionValue="name"
                onChange={(e) => handleMatchFieldChange(rowData.url, e.value)}
            />
        </div>
    );

    const writeHostSearchConfig = (url, changes, isSelected) => {
        if (!campaign.settings.site_search_job.search_fields_based_url) {
            campaign.settings.site_search_job.search_fields_based_url = {};
        }

        campaign.settings.site_search_job.search_fields_based_url[url] = {
            ...normalizeHostSearchConfig(campaign.settings.site_search_job.search_fields_based_url[url]),
            selected: isSelected === undefined ? searchCompetitors.some(s => s.url === url) : isSelected,
            ...changes
        };
    };

    const handleSearchFieldChange = (url, value) => {
        const selectedValues = Array.isArray(value) ? value : [];

        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, selected_fields: selectedValues }
                    : item
            )
        );

        writeHostSearchConfig(url, {
            fields: selectedValues.map(code => {
                const found = search_fields.find(f => f.code === code);
                return found || { code };
            })
        });
    };

    const targetOptions = [
        {value: 'competitor', label: t('suggestion.target_competitor')},
        {value: 'supplier', label: t('suggestion.target_supplier')}
    ];

    const targetTemplate = (rowData) => (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <Dropdown
                value={rowData.target || 'competitor'}
                options={targetOptions}
                onChange={(e) => handleTargetChange(rowData.url, e.value)}
            />
        </div>
    );

    const handleTargetChange = (url, value) => {
        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, target: value }
                    : item
            )
        );

        writeHostSearchConfig(url, { target: value });
    };

    const handleMatchFieldChange = (url, value) => {
        const selectedValues = Array.isArray(value) ? value : [];

        setCampaignCompetitors(prev =>
            prev.map(item =>
                item.url === url
                    ? { ...item, selected_match_fields: selectedValues }
                    : item
            )
        );

        if (!campaign.settings.site_search_job.match_fields_based_url) {
            campaign.settings.site_search_job.match_fields_based_url = {};
        }

        campaign.settings.site_search_job.match_fields_based_url[url] = {
            selected: searchCompetitors.some(s => s.url === url),
            fields: selectedValues.map(name => {
                const found = matchFields.find(f => f.name === name);
                return found || { name };
            })
        };
    };

    return (
        <div className="p-grid p-fluid">
            <Panel header={t('suggestion.time_settings')} style={{height:'100%'}}>
                <JobSchedulingTimeSetting job={site_search_job}/>
            </Panel>

            <p></p>

            <Panel header={t('campaign.generic_setting')} style={{height:'100%'}}>
                <WebSearchSetting webSearchSetting={site_search_job}/>
            </Panel>

            <p></p>
            {loading ?
                <div className="p-col-12" style={{textAlign: 'center'}}>
                    <Loading/>
                </div> :
                <Panel header={t('suggestion.competitor_settings')} style={{height:'100%'}}>
                    <div className="p-col-12 p-xl-12">
                        <DataTable
                            value={campaignCompetitors}
                            dataKey="url"
                            selection={searchCompetitors}
                            onSelectionChange={e => {
                                const selected = e.value.map(s => s.url);

                                setSearchCompetitors(e.value);

                                site_search_job.competitor_urls = selected;

                                if (!campaign.settings.site_search_job.search_fields_based_url) {
                                    campaign.settings.site_search_job.search_fields_based_url = {};
                                }

                                if (!campaign.settings.site_search_job.match_fields_based_url) {
                                    campaign.settings.site_search_job.match_fields_based_url = {};
                                }

                                campaignCompetitors.forEach(c => {
                                    const existingFields = campaign.settings.site_search_job.match_fields_based_url?.[c.url]?.fields ||
                                        campaign.settings.site_search_job.match_fields_based_url?.[c.url] ||
                                        [];

                                    writeHostSearchConfig(c.url, {}, selected.includes(c.url));

                                    campaign.settings.site_search_job.match_fields_based_url[c.url] = {
                                        selected: selected.includes(c.url),
                                        fields: existingFields
                                    };
                                });
                            }}
                            selectionMode="checkbox"
                        >
                            <Column selectionMode="multiple" style={{ width: '3em' }} />
                            <Column header={t('table_headers.index')} body={IndexTemplate} />
                            <Column field="url" body={urlTemplate} header={t('campaign.competitors.url')} />
                            <Column body={searchFieldsTemplate} header={t('suggestion.search_fields')} />
                            <Column body={matchFieldsTemplate} header={t('suggestion.match_fields')} />
                            {campaign.settings?.supplier_module?.is_active &&
                                <Column body={targetTemplate} header={t('suggestion.target')} />}
                        </DataTable>
                    </div>
                </Panel>
            }
        </div>
    );

};
export default (JobSchedulingSiteSearchSetting)
